import clsx from "clsx";
import { useCallback, useRef, useState } from "react";

import {
  Badge,
  Button,
  Displayer,
  Switch,
} from "@smart/itops-sb-design-system-dom";

import { SectionChangeEntry } from "../utils";

type PublishFormPopupProps = {
  onPublishClick: () => void;
  sectionChangeEntries: SectionChangeEntry[];
  highlightChanges: boolean;
  onHighlightChangesChange: (value: boolean) => void;
};

const LEAVE_DELAY_MS = 150;

export const PublishFormPopup = ({
  onPublishClick,
  sectionChangeEntries,
  highlightChanges,
  onHighlightChangesChange,
}: PublishFormPopupProps) => {
  const [expanded, setExpanded] = useState(false);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const clearLeaveTimeout = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = undefined;
    }
  }, []);

  const handleEnter = useCallback(() => {
    clearLeaveTimeout();
    setExpanded(true);
  }, [clearLeaveTimeout]);

  const handleLeave = useCallback(() => {
    clearLeaveTimeout();
    leaveTimeoutRef.current = setTimeout(() => {
      setExpanded(false);
    }, LEAVE_DELAY_MS);
  }, [clearLeaveTimeout]);

  return (
    <div
      data-testid="publish-form-popup"
      className="flex flex-col w-[28rem] bg-white rounded py-[1.6rem] px-[2.4rem] shadow-dropdownMenu gap-medium"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {expanded && (
        <div
          data-testid="publish-form-review-options"
          className="flex flex-col gap-medium"
        >
          <div className="text-caption font-semibold uppercase text-neutral-600 tracking-wide">
            Review options
          </div>
          <div className="flex items-start justify-between gap-medium">
            <div className="flex flex-col gap-[0.2rem]">
              <span className="text-ui font-semibold text-neutral-850">
                Highlight changes
              </span>
              <span className="text-caption text-neutral-600">
                Mark edited fields in the form
              </span>
            </div>
            <Switch
              toggled={highlightChanges}
              onClick={() => onHighlightChangesChange(!highlightChanges)}
              dataTestId="highlight-changes-toggle"
            />
          </div>
          {sectionChangeEntries.length > 0 && (
            <>
              <div className="border-t border-neutral-100 border-solid" />
              <ul
                data-testid="publish-form-section-changes-list"
                className="flex flex-col gap-[0.8rem] max-h-[16rem] min-h-0 overflow-y-auto"
              >
                {sectionChangeEntries.map((entry) => (
                  <li
                    key={entry.uri}
                    className="flex items-center justify-between gap-medium"
                  >
                    <div
                      className={clsx(
                        "text-ui font-semibold truncate",
                        entry.removed
                          ? "text-neutral-500 line-through"
                          : "text-neutral-850",
                      )}
                    >
                      <Displayer
                        paragraphTag="span"
                        value={entry.title}
                        fallback="Section"
                        disableLinks
                      />
                    </div>
                    <Badge
                      label={String(entry.count)}
                      theme="information"
                      className="shrink-0"
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="border-t border-neutral-100 border-solid" />
        </div>
      )}
      <div className="flex items-center gap-[0.8rem] text-caption font-semibold text-yellow-850 bg-yellow-150 rounded-[0.8rem] px-[1.6rem] py-[1rem]">
        <span aria-hidden="true">&bull;</span>
        Unpublished changes
      </div>
      <Button text="Publish" onClick={onPublishClick} className="w-full" />
    </div>
  );
};

export type { PublishFormPopupProps };
